import { Component, HostListener, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { Menu } from 'primeng/menu';
import { CommonModule } from '@angular/common';
import { Messenger } from '@eda/features/messenger/messenger';
import { AgentHeader } from '@etninfinite/aiops';
import { headerMenu, sidebarConfig } from './header.config';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { AboutDialog } from '@eda/features/about-dialog/about-dialog';
import { TermsOfUseDialog } from '@eda/features/terms-of-use-dialog/terms-of-use-dialog';
import { ConversationSummary } from '@eda/shared/models/chat-history.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-main',
  imports: [CommonModule, AgentHeader, MenuModule, Messenger, AboutDialog, TermsOfUseDialog, TranslatePipe],
  templateUrl: './main.html',
})
export class Main implements OnDestroy {
  @ViewChild('menu') menu!: Menu;

  private readonly translate = inject(TranslateService);
  private readonly langChangeSubscription = this.translate.onLangChange.subscribe(() => {
    this.buildMenuItems();
  });

  @HostListener('window:resize')
  onResize() {
    this.menu?.hide();
  }

  headerMenuItems = headerMenu;
  sidebarConfig = sidebarConfig;
  showAboutDialog: boolean = false;
  showTermsOfUseDialog: boolean = false;
  selectedConversationSignal = signal<ConversationSummary | null>(null);
  items: MenuItem[] = [];

  constructor() {
    this.buildMenuItems();
  }

  ngOnDestroy(): void {
    this.langChangeSubscription.unsubscribe();
  }

  private buildMenuItems(): void {
    this.items = [
      {
        id: 'terms',
        type: 'click',
        label: this.buildMenuLabel(
          '<path fill="none" d="M0 0h24v24H0z"></path><path d="M19 5v14H5V5h14m1.1-2H3.9c-.5 0-.9.4-.9.9v16.2c0 .4.4.9.9.9h16.2c.4 0 .9-.5.9-.9V3.9c0-.5-.5-.9-.9-.9zM11 7h6v2h-6V7zm0 4h6v2h-6v-2zm0 4h6v2h-6zM7 7h2v2H7zm0 4h2v2H7zm0 4h2v2H7z"></path>',
          this.translate.instant('MAIN.MENU.TERMS')
        ),
        command: () => this.openTermsOfUseDialog(),
        escape: false,
        title: this.translate.instant('MAIN.MENU.TERMS'),
      },
      {
        id: 'about',
        type: 'click',
        label: this.buildMenuLabel(
          '<path fill="none" d="M0 0h24v24H0V0z"></path><path d="M11 7h2v2h-2V7zm0 4h2v6h-2v-6zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"></path>',
          this.translate.instant('MAIN.MENU.ABOUT')
        ),
        command: () => this.openAboutDialog(),
        escape: false,
        title: this.translate.instant('MAIN.MENU.ABOUT'),
      },
    ];
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }

  private buildMenuLabel(iconPathMarkup: string, text: string): string {
    const safeText = this.escapeHtml(text);
    return `
      <span class="flex">
        <svg class="mr-2" stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 24 24" height="20" width="20" xmlns="http://www.w3.org/2000/svg">
          ${iconPathMarkup}
        </svg>
        ${safeText}
      </span>
    `;
  }

  onConversationSelected(conversation: ConversationSummary) {
    this.selectedConversationSignal.set(conversation);
  }

  startNewChat() {
    window.location.reload();
  }

  openTermsOfUseDialog() {
    this.showTermsOfUseDialog = true;
  }

  openAboutDialog() {
    this.showAboutDialog = true;
  }
}
